import { HeartHandshake, Map, MessageCircleHeart, Share2 } from "lucide-react";

import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";

const familyBenefits = [
  {
    title: "Peace of mind",
    icon: HeartHandshake,
    body: "Families receive a clearer picture of each journey, helping to reduce uncertainty through visible, structured day-to-day transport support.",
  },
  {
    title: "Live journey tracking",
    icon: Map,
    body: "Track progress through key milestones so carers and guardians can feel more connected to the journey as it happens.",
  },
  {
    title: "Share with family",
    icon: Share2,
    body: "Extend reassurance by allowing key family members to stay informed through shared visibility where appropriate.",
  },
  {
    title: "Clear communication",
    icon: MessageCircleHeart,
    body: "Support reassurance with clearer updates and a service model designed around safeguarding, consistency, and care.",
  },
];

export const FamiliesPage = () => {
  return (
    <div>
      <PageHero
        eyebrow="For families"
        title="Reassurance for families through clearer visibility and safer handovers."
        description="CareFleet gives parents and guardians a clearer picture of every journey through live updates, structured communication, and a safeguarding-first transport model built for day-one clarity."
        highlights={["Peace of mind", "Tracking visibility", "Shared reassurance", "Secure handovers"]}
        actions={[{ label: "Request Demo", to: "/contact", testId: "families-request-demo-button" }]}
        baseTestId="families-hero"
        visual={
          <div className="dashboard-surface p-8">
            <div className="rounded-[28px] border border-white/10 bg-white/5 p-6" data-testid="families-journey-card">
              <p className="text-sm uppercase tracking-[0.2em] text-slate-300">Journey status</p>
              <p className="mt-3 font-heading text-3xl font-semibold text-white">On route • 08:12</p>
              <p className="mt-3 text-base leading-7 text-slate-300">
                Pickup confirmed, journey progressing, and handover checkpoint ready for completion.
              </p>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {[
                "Live map visibility",
                "Handover confirmations",
                "Family-friendly updates",
                "Trusted safeguarding workflows",
              ].map((item) => (
                <div key={item} className="rounded-3xl border border-white/10 bg-white/5 p-5" data-testid={`families-feature-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <p className="font-heading text-xl font-semibold text-white">{item}</p>
                </div>
              ))}
            </div>
          </div>
        }
      />

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-12">
          <SectionHeading
            eyebrow="Family experience"
            title="Built to make journeys feel more understandable and less stressful."
            description="Families need reassurance that transport is safe, monitored, and responsive. CareFleet combines human care with digital checkpoints that make that reassurance more tangible."
            testId="families-benefits-heading"
          />
          <div className="grid gap-6 lg:grid-cols-2">
            {familyBenefits.map((benefit) => {
              const Icon = benefit.icon;
              return (
                <Reveal key={benefit.title}>
                  <div className="soft-card h-full p-8" data-testid={`families-benefit-${benefit.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-mist text-brand-navy">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h2 className="mt-6 font-heading text-3xl font-semibold text-slate-950">{benefit.title}</h2>
                    <p className="mt-4 text-base leading-8 text-slate-600">{benefit.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};